import { useCallback, useEffect, useMemo, useState } from 'react';
import { LeaderboardTab, LeaderboardRow } from '../types';
import { getColumns } from '../constants';
import { useLeaderboardParticipants } from '../../../hooks/useLeaderboardParticipants';
import { useLeaderboardDistricts } from '../../../hooks/useLeaderboardDistricts';
import { useLeaderboardOffices } from '../../../hooks/useLeaderboardOffices';
import { useActiveLeaderboardEvent } from '../../../hooks/useActiveLeaderboardEvent';

export const useAdminLeaderboard = () => {
  const [activeTab, setActiveTab] = useState<LeaderboardTab>('participants');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [hasLoaded, setHasLoaded] = useState(false);
  const { activeEvent } = useActiveLeaderboardEvent();

  const {
    participants,
    totalPages: participantsTotalPages,
    pageNumber: participantsPage,
    setPageNumber: setParticipantsPage,
    isLoading: participantsLoading,
    refetch,
  } = useLeaderboardParticipants(
    activeEvent?.id,
    activeTab === 'participants' ? debouncedSearch : undefined,
    startDate,
    endDate
  );
  const {
    districts,
    totalPages: districtsTotalPages,
    pageNumber: districtsPage,
    setPageNumber: setDistrictsPage,
    isLoading: districtsLoading,
  } = useLeaderboardDistricts(
    activeEvent?.id,
    activeTab === 'areas' ? debouncedSearch : undefined,
    startDate,
    endDate
  );
  const {
    offices,
    totalPages: officesTotalPages,
    pageNumber: officesPage,
    setPageNumber: setOfficesPage,
    isLoading: officesLoading,
  } = useLeaderboardOffices(
    activeEvent?.id,
    activeTab === 'offices' ? debouncedSearch : undefined,
    startDate,
    endDate
  );

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(handle);
  }, [search]);

  const startDateTime = startDate?.getTime();
  const endDateTime = endDate?.getTime();

  useEffect(() => {
    if (activeTab === 'participants') setParticipantsPage(1);
    else if (activeTab === 'areas') setDistrictsPage(1);
    else setOfficesPage(1);
    setHasLoaded(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, activeTab, startDateTime, endDateTime]);

  useEffect(() => {
    setHasLoaded(false);
  }, [startDateTime, endDateTime]);

  useEffect(() => {
    setHasLoaded(false);
  }, [activeEvent?.id]);
  const [isAddReviewOpen, setIsAddReviewOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [isAddCaseOpen, setIsAddCaseOpen] = useState(false);
  const [isDeleteReviewOpen, setIsDeleteReviewOpen] = useState(false);
  const [isCasesModalOpen, setIsCasesModalOpen] = useState(false);
  const [selectedUserName, setSelectedUserName] = useState<string | null>(null);
  const [selectedUserAgencyNames, setSelectedUserAgencyNames] = useState<string | null>(null);

  const handleAddReviewClick = useCallback(
    (userId: string) => {
      const user = (participants || []).find((u: any) => String(u.id) === String(userId));
      if (user) {
        setSelectedUserId(String(userId));
        setSelectedUserName(`${user.firstName} ${user.lastName}`);
      }
      setIsAddReviewOpen(true);
    },
    [participants]
  );

  const closeAddReviewModal = () => {
    setIsAddReviewOpen(false);
    setSelectedUserId(null);
    setSelectedUserName(null);
  };

  const handleAddCaseClick = useCallback(
    (userId?: string, agency?: string, name?: string) => {
      if (userId) {
        setSelectedUserId(String(userId));
      }
      if (name) {
        setSelectedUserName(name);
      } else if (userId) {
        const user = (participants || []).find((u: any) => String(u.id) === String(userId));
        if (user) setSelectedUserName(`${user.firstName} ${user.lastName}`);
      }
      if (agency) {
        setSelectedUserAgencyNames(agency);
      } else if (userId) {
        const user = (participants || []).find((u: any) => String(u.id) === String(userId));
        if (user) setSelectedUserAgencyNames(user.agencyNames || '');
      }
      setIsAddCaseOpen(true);
    },
    [participants]
  );

  const closeAddCaseModal = () => {
    setIsAddCaseOpen(false);
    setSelectedUserAgencyNames(null);
  };

  const handleDeleteReviewClick = useCallback(
    (userId: string) => {
      const user = (participants || []).find((u: any) => String(u.id) === String(userId));
      if (user) {
        setSelectedUserId(String(userId));
        setSelectedUserName(`${user.firstName} ${user.lastName}`);
      }
      setIsDeleteReviewOpen(true);
    },
    [participants]
  );

  const closeDeleteReviewModal = () => {
    setIsDeleteReviewOpen(false);
    setSelectedUserId(null);
    setSelectedUserName(null);
  };

  const handleViewCasesClick = useCallback(
    (userId: string) => {
      const user = (participants || []).find((u: any) => String(u.id) === String(userId));
      if (user) {
        setSelectedUserId(String(userId));
        setSelectedUserName(`${user.firstName} ${user.lastName}`);
      }
      setIsCasesModalOpen(true);
    },
    [participants]
  );

  const closeCasesModal = () => {
    setIsCasesModalOpen(false);
    setSelectedUserId(null);
    setSelectedUserName(null);
  };

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId as LeaderboardTab);
    setSearch('');
    setDebouncedSearch('');
  };

  const columns = useMemo(
    () =>
      getColumns(
        activeTab,
        handleAddReviewClick,
        handleAddCaseClick,
        handleDeleteReviewClick,
        handleViewCasesClick
      ),
    [
      activeTab,
      handleAddReviewClick,
      handleAddCaseClick,
      handleDeleteReviewClick,
      handleViewCasesClick,
    ]
  );

  const mapToRow = (p: any): LeaderboardRow => ({
    id: p.id,
    rating: p.rank,
    name: `${p.firstName} ${p.lastName}`,
    hours: String(p.totalHours ?? 0),
    clients: String(p.totalClients ?? 0),
    totalReviews: String(p.reviewsCount ?? 0),
    score: `${p.totalScore ?? 0} points`,
    area: p.districtNames || '',
    office: p.officeNames || '',
    agency: p.agencyNames || '',
    lastUpdate: new Date(p.lastUpdated).toLocaleDateString(),
  });

  const mapDistrictToRow = (d: any): LeaderboardRow => ({
    id: d.id,
    rating: d.rank,
    name: d.districtName,
    hours: String(d.totalHours ?? 0),
    clients: String(d.totalClients ?? 0),
    totalReviews: String(d.totalReviews ?? 0),
    score: `${d.totalScore ?? 0} points`,
    area: d.districtName,
    office: '',
    agency: '',
    lastUpdate: new Date(d.lastUpdated).toLocaleDateString(),
  });

  const mapOfficeToRow = (o: any): LeaderboardRow => ({
    id: o.id,
    rating: o.rank,
    name: o.name,
    hours: String(o.totalHours ?? 0),
    clients: String(o.totalClients ?? 0),
    totalReviews: String(o.totalReviews ?? 0),
    score: `${o.totalScore ?? 0} points`,
    area: o.districtName || '',
    office: o.name,
    agency: o.agencyName || '',
    lastUpdate: new Date(o.lastUpdated).toLocaleDateString(),
  });

  const currentData = useMemo<LeaderboardRow[]>(() => {
    if (activeTab === 'participants') return (participants || []).map(mapToRow);
    if (activeTab === 'areas') return (districts || []).map(mapDistrictToRow);
    if (activeTab === 'offices') return (offices || []).map(mapOfficeToRow);
    return [];
  }, [participants, districts, offices, activeTab]);

  const totalPages =
    activeTab === 'participants'
      ? participantsTotalPages
      : activeTab === 'areas'
        ? districtsTotalPages
        : officesTotalPages;

  const currentPage =
    activeTab === 'participants'
      ? participantsPage
      : activeTab === 'areas'
        ? districtsPage
        : officesPage;

  const setCurrentPage = (page: number) => {
    if (activeTab === 'participants') setParticipantsPage(page);
    else if (activeTab === 'areas') setDistrictsPage(page);
    else setOfficesPage(page);
  };

  const isLoading = useMemo(() => {
    if (activeTab === 'participants') return participantsLoading;
    if (activeTab === 'areas') return districtsLoading;
    if (activeTab === 'offices') return officesLoading;
    return false;
  }, [activeTab, participantsLoading, districtsLoading, officesLoading]);

  useEffect(() => {
    if (!isLoading && (participants.length > 0 || districts.length > 0 || offices.length > 0)) {
      setHasLoaded(true);
    }
  }, [isLoading, participants.length, districts.length, offices.length]);

  return {
    activeTab,
    startDate,
    endDate,
    currentPage,
    columns,
    currentData,
    totalPages,
    isLoading: !hasLoaded,
    handleTabChange,
    setStartDate,
    setEndDate,
    setCurrentPage,
    refetchParticipants: refetch,
    search,
    setSearch,
    isAddReviewOpen,
    selectedUserId,
    handleAddReviewClick,
    closeAddReviewModal,
    isAddCaseOpen,
    handleAddCaseClick,
    closeAddCaseModal,
    isDeleteReviewOpen,
    handleDeleteReviewClick,
    closeDeleteReviewModal,
    isCasesModalOpen,
    handleViewCasesClick,
    closeCasesModal,
    selectedUserName,
    selectedUserAgencyNames,
    activeEvent,
  };
};
